// The native disclosure is expanded on desktop and compact on mobile.
export function initializeCollectionFilters(root = document) {
  root.querySelectorAll('[data-collection-filters]').forEach((panel) => {
    if (panel.dataset.filtersReady) return;
    panel.dataset.filtersReady = 'true';
    const mobile = window.matchMedia('(max-width: 760px)');
    const update = () => { panel.open = !mobile.matches; };
    update();
    mobile.addEventListener('change', update);
    initializeFilterNavigation(panel);
  });
}

// Keep the hero, Forms and document intact: only refresh the native filter/results HTML.
function initializeFilterNavigation(panel) {
  const workbench = panel.closest('.collection-workbench');
  const results = workbench?.querySelector('.collection-results');
  if (!workbench || !results) return;
  const path = window.location.pathname;
  const selector = '.facet__value, .collection-facets__reset, .collection-empty a.utility-button--text';
  const historyKey = 'wilgoodCollectionScroll';
  let pending;
  let renderedURL = window.location.href;
  const status = document.createElement('p');
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.style.cssText = 'position:fixed;bottom:80px;left:20px;z-index:30;margin:0;padding:12px 18px;background:#f4f1ea;color:#20221e;box-shadow:0 2px 12px #0002;max-width:calc(100vw - 40px);';
  status.hidden = true;
  workbench.after(status);
  const scrollPosition = () => ({ x: window.scrollX, y: window.scrollY });
  const restoreScroll = ({ x, y }) => window.scrollTo({ left: x, top: y, behavior: 'instant' });
  const saveScroll = () => window.history.replaceState({ ...window.history.state, [historyKey]: scrollPosition() }, '', window.location.href);

  async function render(url, { push = true, position } = {}) {
    pending?.abort();
    const request = new AbortController();
    pending = request;
    const timeout = window.setTimeout(() => request.abort(), 15000);
    workbench.setAttribute('aria-busy', 'true');
    status.hidden = false;
    status.textContent = 'Updating products…';
    try {
      const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Collection unavailable');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextPanel = page.querySelector('.collection-workbench [data-collection-filters]');
      const nextResults = page.querySelector('.collection-workbench .collection-results');
      if (!nextPanel || !nextResults) throw new Error('Collection missing');
      if (pending !== request || !workbench.isConnected) return;

      // Read at response time so a slow request never undoes the visitor's scrolling.
      const targetPosition = position || scrollPosition();
      const disclosures = new Map([...panel.querySelectorAll('.facet')].map(facet => [facet.querySelector('summary')?.textContent.trim(), facet.open]));
      const focused = document.activeElement;
      const focusedLink = workbench.contains(focused) && focused.matches(selector) ? focused : null;
      const focusLabel = focusedLink?.querySelector('span')?.textContent || focusedLink?.textContent;
      const focusGroup = focusedLink?.closest('.facet')?.querySelector('summary')?.textContent.trim();
      if (push) saveScroll();
      panel.innerHTML = nextPanel.innerHTML;
      results.innerHTML = nextResults.innerHTML;
      panel.querySelectorAll('.facet').forEach(facet => {
        const label = facet.querySelector('summary')?.textContent.trim();
        if (disclosures.has(label)) facet.open = disclosures.get(label);
      });
      if (push) window.history.pushState({ ...window.history.state, [historyKey]: targetPosition }, '', url);
      renderedURL = window.location.href;
      if (focusedLink) {
        const replacement = [...workbench.querySelectorAll(selector)].find(link =>
          (link.querySelector('span')?.textContent || link.textContent) === focusLabel &&
          link.closest('.facet')?.querySelector('summary')?.textContent.trim() === focusGroup
        );
        replacement?.focus({ preventScroll: true });
      }
      restoreScroll(targetPosition);
      workbench.dispatchEvent(new CustomEvent('collection:updated', { bubbles: true }));
      status.hidden = true;
    } catch (error) {
      if (pending !== request) return;
      // Keep the current products instead of navigating to a blank/error page.
      if (!push) window.history.replaceState(window.history.state, '', renderedURL);
      status.textContent = 'Products could not be updated. Please try again.';
    } finally {
      window.clearTimeout(timeout);
      if (pending === request) {
        workbench.removeAttribute('aria-busy');
        pending = null;
      }
    }
  }

  workbench.addEventListener('click', event => {
    const link = event.target.closest?.(selector);
    if (!link || !workbench.contains(link) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target || link.hasAttribute('download')) return;
    if (link.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== path) return;
    event.preventDefault();
    render(url.href);
  });
  window.addEventListener('popstate', event => {
    if (!workbench.isConnected || window.location.pathname !== path) return;
    render(window.location.href, { push: false, position: event.state?.[historyKey] || scrollPosition() });
  });
}
if (typeof document !== 'undefined') {
  initializeCollectionFilters();
  document.addEventListener('shopify:section:load', (event) => initializeCollectionFilters(event.target));
}
